/* eslint-disable no-nested-ternary */
import React, { Fragment, useState } from "react";
import "../documentation.css";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { TodoIcon } from "../../assets/Icons";
import CandImage from "../Components/CandImage";
import Initials from "../Components/Initials";
import LoadingSpinner from "../../common/LoadingSpinner";

const PusterlaDocuments = ({
  candidates,
  getReadingListPdf,
  downloadRef,
  images,
  passImage,
  handleOverlay
}) => {
  /**
   * Make call to profile 5 to retrieve desired file for Pusterla
   * @param {String} candidate
   * @param {String} typeCode
   * @param {String} filename
   * @param {String} contentType
   * @param {String} fileExt
   */

  const [loadingTypeCode, setLoadingTypeCode] = useState("");

  const handleDownloadReadingList = async (
    candidate,
    typeCode,
    filename,
    contentType,
    fileExt
  ) => {
    setLoadingTypeCode(typeCode);
    getReadingListPdf(
      candidate.candidateRef,
      candidate.firstName,
      candidate.lastName,
      typeCode,
      filename,
      contentType,
      fileExt
    ).then(() => setLoadingTypeCode(""));
  };

  /**
   *  Mapping of file typecode to file ext
   */
  const typeCodeToExt = new Map();
  typeCodeToExt
    .set("YPDC", ".pdf") // Contract for services, .pdf
    .set("YREU", ".pdf") // Settled Status - EU Settlement Scheme , .pdf (does not come from Profile5 - added by consultant direct to candidates profile)
    .set("YREP", ".pdf") // Pre-Settled Status - EU Settlement Scheme, .pdf (does not come from Profile5 - added by consultant direct to candidates profile)
    .set("JPPT", ".jpg") // Front of passport for RTW, .jpg
    .set("JPVI", ".jpg") // Visa front for RTW, .jpg
    .set("JPVB", ".jpg") // Visa back for RTW, .jpg
    .set("JPID", ".jpg") // Front of ID for RTW, .jpg
    .set("JPPB", ".jpg") // back of ID for RTW, .jpg
    .set("JBIR", ".jpg") // birth certficate, .jpg
    .set("JPNI", ".jpg") // Back of passport for RTW, .jpg
    .set("WRTV", ".docx") // Verfiication RTW, .docx
    .set("YXP", ".pdf") // Share Code Check Perm, .pdf
    .set("YXT", ".pdf") // Share Code Check Temp, .pdf
    .set("YRTW", ".pdf") // Right to Work Approved, .pdf
    .set("JPPE", ".jpg") // Expired Passport, .jpg
    .set("YG1", ".pdf"); // Passport / Birth Certificate, .pdf

  /**
   *  File types requested by Pecan Deluxe.
   */
  const RTW_TYPE_CODES = [
    "YPDC", // Contract for services, .pdf
    "YREU", // Settled Status - EU Settlement Scheme
    "YREP", // Pre-Settled Status - EU Settlement Scheme
    "JPPT", // Front of passport for RTW, .jpg
    "JPVI", // Visa front for RTW, .jpg
    "JPVB", // Visa back for RTW, .jpg
    "JPID", // Front of ID for RTW, .jpg
    "JPPB", // back of ID for RTW, .jpg
    "JBIR", // birth certficate, .jpg
    "JPNI", // Back of passport for RTW, .jpg
    "WRTV", // Verfiication RTW, .docx
    "YXP", // Share Code Check Perm, .pdf
    "YXT", // Share Code Check Temp, .pdf
    "YRTW", // Right to Work Approved, .pdf
    "JPPE", // Expired Passport, .pdf
    "YG1" // Passport / Birth Certificate, .pdf
  ];

  /**
   * Retrieves only RTW files from candidate files.
   * @param {Object} candidate
   * @returns {Array<Array<string, string>} Array of Array [[typecode, title], [...]]
   */
  const candidateHasRTWDocs = candidate => {
    let rtwOnly = [];
    if (candidate && candidate.rtwDocs) {
      const entries = Object.entries(candidate.rtwDocs);
      rtwOnly = entries.filter(row => {
        const [typeCode] = row;
        return RTW_TYPE_CODES.includes(typeCode);
      });
    }
    return rtwOnly;
  };

  const simpleDownloadStyles = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "14px",
    width: "80%",
    backgroundColor: "white",
    padding: "8px",
    borderRadius: "5px",
    border: "1px solid #2196f3"
  };

  /**
   * Return the correct content type for file
   * @param {String} ext
   * @returns {String}
   */
  const getContentType = ext => {
    if (ext === ".docx")
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    if (ext === ".pdf") return "application/pdf";
    return "image/jpg";
  };

  /**
   * Remove any illegal character(s) (e.g. -) which can prevent downloads when downloading files to a users computer
   */
  const cleanFilename = filename => filename.replace(/\W+/g, " ");

  const DRIVING_DOCS = [
    { code: "YR05", name: "Initial Licence Check", ext: ".pdf" }, // CPC Front  // "YR05", //  Initial Licence Check
    { code: "Y3LC", name: "3 Month Licence Check", ext: ".pdf" }, // "Y3LC", //  3 Month Licence Check
    { code: "JCPC", name: "CPC Front", ext: ".jpg" }, // CPC Front
    { code: "JCPB", name: "CPC Back", ext: ".jpg" }, // CPC Back
    { code: "JDIG", name: "Digi Card", ext: ".jpg" }, // Digi Card
    { code: "JPDL", name: " Drivers Licence Front", ext: ".jpg" }, // Drivers Licence Front
    { code: "JDLB", name: "Drivers Licence Back", ext: ".jpg" }, // Drivers Licence Back
    { code: "YCPC", name: "CPC Front and Back", ext: ".pdf" }, // Drivers Licence Back
    { code: "YDIG", name: "Digi Card", ext: ".pdf" }, // Drivers Licence Back
    { code: "YG1A", name: "Drivers Licence Front", ext: ".pdf" }, // Drivers Licence Back
    { code: "YLIC", name: "Drivers Licence Back", ext: ".pdf" } // Drivers Licence Back
  ];

  /**
   * Return the dropdown of candidate RTW files (if) avaliable for download or red cross icon.
   * @param {Object} candidate
   * @returns JSX
   */
  const RTWDocs = candidate => {
    const rtwDocs = candidateHasRTWDocs(candidate);
    if (rtwDocs.length > 0) {
      return (
        <div className="tw-w-full tw-flex tw-justify-center">
          <Menu
            style={{ width: "100%" }}
            as="div"
            className="tw-relative tw-inline-block sm:tw-pl-2 tw-text-left"
          >
            <Menu.Button
              style={{ border: "1px solid #2196f3" }}
              className="tw-flex tw-justify-center tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-p-2 tw-bg-white tw-text-sm tw-text-gray-700 "
            >
              RTW Downloads
              <ChevronDownIcon
                className="tw--mr-1 tw-ml-2 tw-h-5 tw-w-5"
                aria-hidden="true"
              />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="tw-transition tw-ease-out tw-duration-100"
              enterFrom="tw-transform tw-opacity-0 tw-scale-95"
              enterTo="tw-transform tw-opacity-100 tw-scale-100"
              leave="tw-transition tw-ease-in tw-duration-75"
              leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
              leaveTo="tw-transform tw-opacity-0 tw-scale-95"
            >
              <Menu.Items className=" tw-origin-top-right tw-relative tw-w-full tw-right-0 tw-mt-2 tw-w-36 tw-rounded-md tw-shadow-lg tw-bg-white tw-ring-1 tw-ring-black tw-ring-opacity-5 tw-focus:outline-none">
                <div className="tw-py-1">
                  {rtwDocs.map(row => {
                    const [typeCode, title] = row;
                    const ext = typeCodeToExt.get(typeCode);
                    return (
                      <Menu.Item key={typeCode}>
                        {({ active }) => (
                          <div
                            // Set correct file ext + MIME type for RTW content
                            onClick={() =>
                              handleDownloadReadingList(
                                candidate,
                                typeCode,
                                cleanFilename(title),
                                getContentType(ext),
                                ext
                              )
                            }
                            href="#"
                            className={`${
                              active
                                ? "tw-bg-gray-100 tw-text-gray-900 tw-no-underline"
                                : "tw-no-underline tw-text-gray-700"
                            }
                              tw-block tw-px-4 tw-py-2 tw-text-sm tw-no-underline tw-cursor-pointer `}
                          >
                            {title}
                          </div>
                        )}
                      </Menu.Item>
                    );
                  })}
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      );
    }
    return (
      <div className="tw-w-full tw-flex tw-justify-center">
        <TodoIcon />
      </div>
    );
  };

  const [highlightBtn, setHighlightBtn] = useState("");
  const [display, setDisplay] = useState(false);
  const [selected, setSelected] = useState(null);
  const setHighligtning = key =>
    highlightBtn === key ? setHighlightBtn("") : setHighlightBtn(key);

  const ViewButton = ({ id, fn, num, disabled = false }) => (
    <div className="tw-flex tw-flex-col tw-items-center tw-content-center tw-w-1/2 tw-py-10">
      <button
        disabled={disabled}
        className={`${id === highlightBtn &&
          !disabled &&
          "tw-border-blue-400 tw-text-white tw-bg-blue-400 tw-border-2"} tw-bg-blue-200' tw-bg-blue-100 tw-p-2 lg:tw-w-1/2 tw-w-full tw-rounded-lg ${id !==
          highlightBtn &&
          !disabled &&
          "hover:tw-bg-blue-200 hover:tw-border-blue-500"} tw-border-blue-300 tw-border-2 tw-cursor-pointer`}
        onClick={fn}
      >
        <span className="md:tw-inline tw-hidden">View</span>
        <span className="md:tw-hidden tw-inline">{num}</span>
      </button>
    </div>
  );

  const populateDropRow = candidate =>
    DRIVING_DOCS.map((doc, i) => {
      const read =
        candidate &&
        candidate.nonReadingListDocs &&
        candidate.nonReadingListDocs[doc.code];

      return (
        read && (
          <div
            key={i}
            className="tw-bg-gray-50 tw-flex tw-flex-col tw-content-center tw-text-center tw-p-2 tw-m-4 tw-h-40 tw-min-h-fit tw-w-52 tw-border-2 tw-items-center"
          >
            <p className="tw-mb-1">{doc.name}</p>
            <div
              className="tw-w-full tw-h-full tw-flex"
              style={{ alignItems: "center" }}
            >
              <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-content-center">
                <button
                  onClick={() =>
                    handleDownloadReadingList(
                      candidate,
                      doc.code,
                      doc.name,
                      getContentType(doc.ext),
                      doc.ext
                    )
                  }
                  style={simpleDownloadStyles}
                >
                  {loadingTypeCode === doc.code ? (
                    <LoadingSpinner />
                  ) : (
                    "Download"
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      );
    });

  const checkIfShow = c => {
    const docsTypeCodes = Object.keys(c.nonReadingListDocs);
    return DRIVING_DOCS.find(el => docsTypeCodes.includes(el.code));
  };

  return (
    <div>
      <div className="tw-rounded-md tw-overflow-x-auto ">
        <div className="tw-align-middle tw-rounded-md tw-inline-block tw-min-w-full">
          <div className="tw-shadow tw-rounded-md tw-overflow-hidden tw-border-b tw-border-gray-200 ">
            <table className="tw-overflow-x tw-border-collapse tw-min-w-full tw-border-2 tw-border-gray-500 tw-rounded-md tw-mb-8 tw-divide-y tw-divide-gray-200">
              <thead className="tw-border-collapse tw-bg-gray-50 tw-rounded-md tw-border-0 tw-border-gray-500 ">
                <tr>
                  <th
                    scope="tw-col"
                    style={{ width: "30%" }}
                    className="tw-py-4 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-left tw-text-md tw-font-medium tw-text-gray-500 tw-text-center tw-tracking-wider"
                  >
                    Worker
                  </th>
                  <th
                    scope="col"
                    style={{ width: "20%" }}
                    className="tw-py-4 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-left tw-text-md tw-font-medium tw-text-gray-500 tw-text-center tw-tracking-wider"
                  >
                    Right to Work Documents
                  </th>
                  <th
                    scope="col"
                    style={{ width: "25%" }}
                    className="tw-py-4 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-left tw-text-md tw-font-medium tw-text-gray-500 tw-text-center tw-tracking-wider"
                  >
                    Contract for Services
                  </th>
                  <th
                    scope="tw-col"
                    style={{ width: "25%" }}
                    className="tw-py-4 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-left tw-text-md tw-font-medium tw-text-gray-500 tw-text-center tw-tracking-wider"
                  >
                    Documents
                  </th>
                </tr>
              </thead>
              <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
                {candidates.map((c, i) => (
                  <Fragment key={i}>
                    <tr className="reading-list__rows" key={i * 100}>
                      <td className="tw-text-sm">
                        <div className="tw-flex lg:tw-flex-row tw-flex-col tw-w-full lg:tw-items-center md:tw-text-left tw-text-center">
                          {images && images[c.candidateRef] ? (
                            <CandImage
                              handleOverlay={handleOverlay}
                              passImage={passImage}
                              image={images[c.candidateRef]}
                              firstName={c.firstName}
                            />
                          ) : (
                            <Initials
                              firstName={c.firstName}
                              lastName={c.lastName}
                            />
                          )}
                          <p className="tw-font-bold tw-font-lg lg:tw-pl-0 tw-pl-1">
                            {c.firstName} {c.lastName}
                          </p>
                        </div>
                      </td>
                      <td className="tw-p-10">{RTWDocs(c)}</td>
                      <td className="tw-p-10">
                        {c.nonReadingListDocs && c.nonReadingListDocs.YZ05 ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center"
                            }}
                          >
                            <button
                              onClick={() =>
                                handleDownloadReadingList(
                                  c,
                                  "YZ05",
                                  `Contract For Services`,
                                  "application/pdf",
                                  "pdf"
                                )
                              }
                              style={simpleDownloadStyles}
                            >
                              Download
                            </button>
                          </div>
                        ) : (
                          <div className="tooltip">
                            <TodoIcon />
                          </div>
                        )}
                      </td>
                      <td>
                        <div
                          className={`tw-flex tw-min-h-full tw-justify-center`}
                        >
                          <ViewButton
                            disabled={!checkIfShow(c)}
                            id={`${i}${c.lastName}L1`}
                            fn={() => {
                              setHighligtning(`${i}${c.lastName}L1`);
                              setDisplay("Documents");
                              if (display === "Documents" && selected === i) {
                                setSelected(null);
                                setDisplay("");
                              } else {
                                setSelected(i);
                              }
                            }}
                            num="L1"
                          />
                        </div>
                      </td>
                    </tr>
                    {selected === i ? (
                      <tr>
                        <td colSpan="6">
                          <div className="tw-flex tw-min-w-full tw-overflow-y-auto tw-flex-wrap tw-pb-4 tw-px-0 tw-justify-center tw-items-center">
                            {/* TODO Pass here all the sops the person has using timestamp to show what is read + signed */}
                            {populateDropRow(c)}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
            <a className="d-none" ref={downloadRef}></a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PusterlaDocuments;
